import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Clock, Pin, PinOff, X } from "lucide-react";

const slug = (q) => q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export function useSavedSearches(open) {
  const [saved, setSaved] = useState({ recent: [], pinned: [], limits: { recent: 8, pinned: 10 } });
  const reload = useCallback(() => api.get("/global-search/saved").then(({ data }) => setSaved(data)).catch(() => {}), []);
  useEffect(() => { if (open) reload(); }, [open, reload]);
  const pin = async (q) => {
    try { await api.post("/global-search/saved/pin", { q }); toast.success("Search pinned"); reload(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not pin search"); }
  };
  const unpin = async (q) => { await api.delete("/global-search/saved/pin", { params: { q } }).catch(() => {}); reload(); };
  const forget = async (q) => { await api.delete("/global-search/saved/recent", { params: { q } }).catch(() => {}); reload(); };
  const isPinned = (q) => saved.pinned.some((p) => p.q.toLowerCase() === q.trim().toLowerCase());
  return { saved, pin, unpin, forget, isPinned };
}

const Chip = ({ q, icon: Icon, tone, onPick, actions, testId }) => (
  <span data-testid={testId}
    className={`group inline-flex items-center gap-1 rounded-full border pl-2.5 pr-1 py-0.5 text-[12px] transition-colors ${tone}`}>
    <Icon className="h-3 w-3 shrink-0 opacity-70" />
    <button type="button" onClick={() => onPick(q)} className="max-w-[180px] truncate py-0.5" data-testid={`${testId}-pick`}>{q}</button>
    {actions}
  </span>
);

const IconBtn = ({ onClick, title, testId, children }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} title={title} aria-label={title} data-testid={testId}
    className="h-5 w-5 grid place-items-center rounded-full text-slate-400 hover:text-slate-800 hover:bg-white/80">
    {children}
  </button>
);

export default function SavedSearches({ store, onPick, onDone }) {
  const { saved } = store;
  const pin = (q) => { store.pin(q); onDone(); };
  const unpin = (q) => { store.unpin(q); onDone(); };
  const forget = (q) => { store.forget(q); onDone(); };
  if (!saved.pinned.length && !saved.recent.length) return null;
  return (
    <div className="px-3 pt-3 pb-1 space-y-2" data-testid="oc-saved-searches">
      {saved.pinned.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400 mb-1.5">
            Pinned searches · {saved.pinned.length}/{saved.limits.pinned}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="oc-pinned-searches">
            {saved.pinned.map((p) => (
              <Chip key={p.q} q={p.q} icon={Pin} onPick={onPick} testId={`oc-pinned-q-${slug(p.q)}`}
                tone="border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100"
                actions={<IconBtn onClick={() => unpin(p.q)} title="Unpin search" testId={`oc-unpin-q-${slug(p.q)}`}><PinOff className="h-3 w-3" /></IconBtn>} />
            ))}
          </div>
        </div>
      )}
      {saved.recent.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400 mb-1.5">Recent searches</div>
          <div className="flex flex-wrap gap-1.5" data-testid="oc-recent-searches">
            {saved.recent.map((r) => (
              <Chip key={r.q} q={r.q} icon={Clock} onPick={onPick} testId={`oc-recent-q-${slug(r.q)}`}
                tone="border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                actions={<>
                  <IconBtn onClick={() => pin(r.q)} title="Pin search" testId={`oc-pin-q-${slug(r.q)}`}><Pin className="h-3 w-3" /></IconBtn>
                  <IconBtn onClick={() => forget(r.q)} title="Remove from recent" testId={`oc-forget-q-${slug(r.q)}`}><X className="h-3 w-3" /></IconBtn>
                </>} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
